import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import GameCard from './GameCard';
import './MyGames.css'
import { sgfStringToState, getGameInfo } from './App';
import SGFList from './SGFList';
import apiClient from "./ApiClient";
import GameInfo from './GameInfo';

/////////////////////////////////
// Reference for game fields:
// 
// let {
//     playerNames,
//     playerRanks,
//     blackName,
//     blackRank,
//     whiteName,
//     whiteRank,
//     gameName,
//     eventName,
//     gameComment,
//     date,
//     result,
//     komi,
//     handicap,
//     size, 
//     gameId, 
//     gameLength, 
//   } = game; 
/////////////////////////////////

const LOADINGGAMES="Loading games..."
const NOGAMESFOUND="No games found."
const GAMES_PER_PAGE = 10; // Number of games per page

export const fetchGames = async (page, games, setMyGames, setError, setStatusMessage, setHasMore, setTotalPages, disablePagination=false) => {

  let apiUrl;

  try {

    if(!disablePagination) {
      console.log(`Fetching games - Page ${page}...`);
      apiUrl = `/sgf/?page=${page}&limit=${GAMES_PER_PAGE}`
    } else {
      console.log(`Fetching all games...`);
      apiUrl = '/sgf/all/'
    }

    const response = await apiClient.get(apiUrl, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('access_token')}`, // Pass the access token
      },
    });

    let data;
    if(!disablePagination) {
      data = response.data.results; 
    } else {
      data = response.data
    }
    console.log("Fetched games:", data);


    if ( ! !!data || data.length === 0) {
      setStatusMessage(NOGAMESFOUND);
      setHasMore(false);
      return;
    }

    let updatedGames = []; 

    // Check for changes in the data
    if ( !!data && JSON.stringify(data) !== JSON.stringify(games) ) {

      updatedGames = data.map((file) => {
        const sgfString = file.sgf_content;
        const gameState = sgfStringToState(sgfString);
        const { gameTrees, gameIndex, gameCurrents } = gameState;
        const tree = gameTrees[gameIndex];
        const currents = gameCurrents[gameIndex];
        const myGameInfo = getGameInfo(tree, currents);
        myGameInfo.sgfString = sgfString;

        return { info: myGameInfo, state: gameState };
      });


      setMyGames(updatedGames);
      if(!disablePagination) {
        setTotalPages(Math.ceil(response.data.count / GAMES_PER_PAGE)); // Calculate total pages
      }
    }

  } catch (error) {
    console.error("Error fetching games:", error.response?.data || error.message);
    setError(error.response?.data || error.message);
  }
};

const MyGames = ( { authToken, loadSGFString, setState }) => {

  const [games, setGames] = useState([]);
  const isFetched = useRef(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [error, setError] = useState(null);
  const [showGameInfoWindow, setShowGameInfoWindow] = useState(false);
  const [focusedGame, setFocusedGame] = useState(undefined);
  const [statusMessage, setStatusMessage] = useState(LOADINGGAMES);
  const navigate = useNavigate();

  useEffect(() => {
    fetchGames(currentPage, games, setGames, setError, setStatusMessage, () => {}, setTotalPages);
  }, [currentPage]); // Fetch when currentPage changes

  useEffect(() => {

    if (!isFetched.current) {
      fetchGames(currentPage, games, setGames, setError, setStatusMessage, () => {}, setTotalPages);
      isFetched.current = true; // Prevent re-fetching
    }
  }, []); // Empty array to prevent infinite re-renders

  const handleClickOnGame = (gameInfo) => {
    // console.log("Clicked gameInfo:", gameInfo); // Debugging: Check what `gameInfo` contains
  
    if (gameInfo?.gameId) {
      loadSGFString(gameInfo.sgfString);

      const game = games.find(game => game.info.gameId === gameInfo.gameId)
      if(!game) {
          console.log(games)
          console.warn("Game with ID not found:", gameInfo.gameId);
          return
      } else { // game with id gameId was found
        setState(game.state)
      }

    } else {
      console.error("Game ID is undefined. Navigation aborted.");
    }
  };

  const updateGame = (updatedGame) => {
    setGames((prevGames) => 
      prevGames.map((game) => 
        game.info.gameId === updatedGame.info.gameId ? updatedGame : game
      )
    );
  };

  return (
    <div className="games-page">
      {error ? (
        <p>Error: {error}</p>
      ) : 
      games.length === 0 ?
        <p>{statusMessage}</p>
       : (
        <div>
          {/* Page Navigation Controls */}
          {totalPages > 1 && <div className="page-navigation-controls">
            <button
              onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              Previous
            </button>
            <span>Page {currentPage} of {totalPages}</span>
            <button
              onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              Next
            </button>
          </div>
          }
          <div className="games-grid">
            {games.map((game) => (
              <GameCard 
                key={game.info.gameId} // Ensure unique key
                game={game} 
                onClick={() => handleClickOnGame(game.info)} 
                setFocusedGame={setFocusedGame}
                setShowGameInfoWindow={setShowGameInfoWindow}
              />
            ))}
          </div>
        </div>
      )}
      {showGameInfoWindow && (
        <div className="dialog-container">
          <GameInfo
            game={games.find((game) => game.info.gameId === focusedGame.info.gameId)}
            setGame={updateGame}
            onClose={() => setShowGameInfoWindow(false)}
          />
        </div>
      )}
    </div>
  );
};

export default MyGames;